import clsx from 'clsx'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import { StyledModalFooter } from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import PriceRowMerchandisePriceStack from 'LuxKit/PricePoints/Merchandise/PriceRowMerchandisePriceStack'
import TextLink from 'LuxKit/TextLink'
import ToggleButton from 'LuxKit/ToggleButton'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useMerchandiseColorOptions from 'hooks/Merchandise/useMerchandiseColorOptions'
import useMerchandiseOptionData from 'hooks/Merchandise/useMerchandiseOptionData'
import useMerchandiseCartActions from 'hooks/Merchandise/useMerchandiseCartActions'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Counter from 'LuxKit/Counter'
import { getMerchandiseSearchUrl, getMerchandiseBrandSearchUrl } from 'components/Merchandise/merchandiseUtils'
import MerchandiseOfferSizeChart from 'components/Merchandise/MerchandiseOfferSizeChart'
import MerchandiseMaxQuantityMessage from 'components/Merchandise/MerchandiseMaxQuantityMessage'
import useMerchandiseBrand from 'hooks/Merchandise/useMerchandiseBrand'
import { MerchandiseColourOption } from 'components/Merchandise/MerchandiseColourOption'
import Modal from 'LuxKit/Modal/Modal'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import MerchandiseFreeShippingLabel from 'components/Merchandise/MerchandiseFreeShippingLabel'
import MerchandiseLowStockLabel from 'components/Merchandise/MerchandiseLowStockLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { rem } from 'polished'
import { ImageParams } from 'components/Common/ResponsiveImage'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useVariantSeller from 'hooks/Merchandise/useVariantSeller'

// Breakpoint dimensions for modal layout
const BREAKPOINTS = [
  { breakpoint: 1080, contentWidth: 480, maxHeight: 520, minHeight: 360 },
  { breakpoint: 1920, contentWidth: 560, maxHeight: 600, minHeight: 400 },
] as const

// First breakpoint is the "desktop" threshold for grid layout
const DESKTOP_BREAKPOINT = BREAKPOINTS[0].breakpoint

// Generate responsive styles from breakpoints
const responsiveStyles = BREAKPOINTS.map(bp => `
  @media (min-width: ${bp.breakpoint}px) {
    width: ${rem(bp.contentWidth)};
  }
`).join('')

const ModalGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: 0 ${rem(20)};
  overflow-x: hidden;
  overflow-y: auto;

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${rem(32)};
    padding: 0 ${rem(24)};
    max-width: none;
    overflow: visible;
  }
`

const StyledImageCarousel = styled(ImageCarousel)<{ $desktopHeight?: number }>`
  height: ${rem(300)};
  width: 100%;

  ${props => BREAKPOINTS.map(bp => `
    @media (min-width: ${bp.breakpoint}px) {
      height: ${props.$desktopHeight
        ? `clamp(${rem(bp.minHeight)}, ${rem(props.$desktopHeight)}, ${rem(bp.maxHeight)})`
        : rem(bp.maxHeight)};
    }
  `).join('')}

  ${responsiveStyles}
`

const DetailsSection = styled(VerticalSpacer)`
  min-width: 0;
  padding-bottom: ${rem(24)};
  overflow-x: hidden;
  overflow-y: auto;
  height: auto;
  max-width: ${rem(700)};

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    align-self: start;
    max-width: none;
  }

  ${BREAKPOINTS.map(bp => `
    @media (min-width: ${bp.breakpoint}px) {
      max-height: ${rem(bp.maxHeight)};
    }
  `).join('')}

  ${responsiveStyles}
`

const Footer = styled(StyledModalFooter)<{ $hasOverflow?: boolean }>`
  padding: ${props => props.$hasOverflow ? rem(20) : `${rem(16)} ${rem(24)} ${rem(32)}`};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.eight};

  &.has-shadow {
    border-top-color: ${props => props.theme.palette.neutral.default.five};
  }
`

const FooterGrid = styled.div`
  display: flex;
  gap: ${rem(8)};
  width: 100%;

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${rem(32)};
  }
`

const FooterSpacer = styled.div`
  display: none;

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    display: block;
  }
`

const FooterButtons = styled(Group)`
  flex: 1;
`

const imageParams: ImageParams = {
  mobileWidth: '480px',
  objectFit: 'contain',
  fit: 'top',
}

interface Props {
  offer: App.MerchandiseOffer
}

function MerchandiseQuickPreviewModal(props: Props) {
  const intl = useIntl()
  const { offer } = props
  const { resolve } = useModalElementContext()
  const closeModal = useCallback(() => resolve(), [resolve])

  const defaultVariant = offer.variants.find(v => (v.inventory ?? 0) > 0) ?? offer.variants[0]
  const [variant, setVariant] = useState<App.MerchandiseOfferVariant>(defaultVariant)
  const [quantity, setQuantity] = useState(1)

  const showModal = useContext(ModalContext)

  // Callback ref - needed because modal portal timing makes useRef null on mount
  const [detailsEl, setDetailsEl] = useState<HTMLDivElement | null>(null)
  const detailsSectionRef = useCallback((node: HTMLDivElement | null) => setDetailsEl(node), [])

  const [hasOverflow, setHasOverflow] = useState(false)
  const [hasReachedEnd, setHasReachedEnd] = useState(true)
  const [detailsHeight, setDetailsHeight] = useState<number>()

  useEffect(() => {
    if (!detailsEl) return

    // Update overflow state only on mount/resize (not scroll)
    const updateOverflow = () => {
      const { scrollHeight, clientHeight } = detailsEl
      setHasOverflow(scrollHeight > clientHeight)
      setDetailsHeight(scrollHeight)
    }

    // Update scroll position on scroll
    const updateScrollPosition = () => {
      const { scrollHeight, clientHeight, scrollTop } = detailsEl
      setHasReachedEnd(scrollTop + clientHeight >= scrollHeight - 1)
    }

    updateOverflow()
    updateScrollPosition()

    const observer = new ResizeObserver(updateOverflow)
    observer.observe(detailsEl)
    detailsEl.addEventListener('scroll', updateScrollPosition)

    return () => {
      observer.disconnect()
      detailsEl.removeEventListener('scroll', updateScrollPosition)
    }
  }, [detailsEl, variant])

  const [brand] = useMerchandiseBrand(offer.brand.id)
  const { seller } = useVariantSeller(offer, variant)

  const soldAndDeliveredTooltipDescription = useMemo(() =>
    intl.formatMessage(
      {
        id: 'A6RAdThOE6',
        defaultMessage: '{sellerName} is a verified seller on Luxury Escapes Shop. All sellers are verified and fulfil orders directly from within Australia.',
        description: 'Tooltip explaining seller verification in merchandise quick preview modal',
      },
      { sellerName: seller.name },
    ),
  [intl, seller.name])

  // Use shared hooks
  const { optionTypeData, onOptionSelect } = useMerchandiseOptionData(offer, variant)
  const { onAddToCart, onBuyNow } = useMerchandiseCartActions(offer, variant, quantity, {
    onAfterAddToCart: closeModal,
    onAfterBuyNow: closeModal,
  })

  const colorOptions = useMerchandiseColorOptions(offer.variants, variant)
  const shouldCombineColorOptions = colorOptions.shouldCombine

  const handleOptionSelect = useCallback((position: number, optionValueName: string) => {
    const newVariant = onOptionSelect(position, optionValueName)
    if (newVariant) {
      setVariant(newVariant)
      setQuantity(prev => Math.min(prev, newVariant.inventory ?? 1))
    }
  }, [onOptionSelect])

  const onVariantChange = useCallback((newVariant: App.MerchandiseOfferVariant) => {
    setVariant(newVariant)
    setQuantity(prev => Math.min(prev, newVariant.inventory ?? 1))
  }, [])

  const onShowSizeChart = useCallback(() => {
    if (offer.sizing) {
      showModal(<Modal
        title={intl.formatMessage({
          id: 'YZnEC0B3Yc',
          defaultMessage: 'Size chart',
          description: 'Modal title for merchandise size chart',
        })}
      >
        <MerchandiseOfferSizeChart sizeChart={offer.sizing} />
      </Modal>)
    }
  }, [intl, offer.sizing, showModal])

  const productDetailsUrl = `/shop/offer/${offer.slug}/${offer.id}`

  return (
    <ModalBase size="auto" onClose={closeModal}>
      <ModalHeader
        title=""
        dismissible
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalGrid>
          <StyledImageCarousel
            $desktopHeight={detailsHeight}
            images={variant.images}
            imageParams={imageParams}
            topLeft={<LabelGroup>
              <MerchandiseFreeShippingLabel offer={offer} />
              <MerchandiseLowStockLabel variants={[variant]} />
            </LabelGroup>}
          />
          <DetailsSection ref={detailsSectionRef} gap={24}>
            <VerticalSpacer gap={24}>
              <div>
                <TextLink
                  size="medium"
                  colour="neutral-two"
                  weight="regular"
                  underline={false}
                  to={brand ? getMerchandiseBrandSearchUrl(brand) : getMerchandiseSearchUrl({ brands: [offer.brand.id] })}
                  endIcon={<LineAngleRightIcon />}
                  onClick={closeModal}
                >
                  {offer.brand.name}
                </TextLink>
              </div>
              <div>
                <Heading variant="heading2">{offer.name}</Heading>
                <Tooltip placement="bottom-start" description={soldAndDeliveredTooltipDescription}>
                  {/* onFocus stops focusin from bubbling to tooltip trigger on modal open */}
                  <span onFocus={e => e.stopPropagation()}>
                    <BodyText variant="medium">
                      <Colour value="neutral-two">
                        {intl.formatMessage({
                          id: 'XOCG3b3M+H',
                          defaultMessage: 'Sold and delivered by:',
                          description: 'Label for seller name in merchandise quick preview modal',
                        })}
                      </Colour>
                      {' '}
                      <b>{seller.name}</b>
                    </BodyText>
                  </span>
                </Tooltip>
              </div>
              <div>
                <TextLink
                  size="medium"
                  to={productDetailsUrl}
                  onClick={closeModal}
                >
                  {intl.formatMessage({
                    id: 'SjKcMGH+Js',
                    defaultMessage: 'View product details',
                    description: 'Link text to view full product details page',
                  })}
                </TextLink>
              </div>
            </VerticalSpacer>

            <PriceRowMerchandisePriceStack
              direction="horizontal"
              size="L"
              variant={variant}
              quantity={quantity}
              displayEarnPoints
            />

            {shouldCombineColorOptions && colorOptions.options.length > 0 && (
              <VerticalSpacer gap={8}>
                <VerticalSpacer gap={4}>
                  {colorOptions.colorLabels.map(({ typeName, selectedName }) => (
                    <BodyText key={typeName} variant="medium">
                      <Colour value="neutral-two">{typeName}:</Colour> <b>{selectedName}</b>
                    </BodyText>
                  ))}
                </VerticalSpacer>
                <Group direction="horizontal" gap={12} wrap="wrap">
                  {colorOptions.options.map(option => (
                    <MerchandiseColourOption
                      key={option.key}
                      interactive
                      size={20}
                      colors={option.colors}
                      selected={option.isSelected}
                      onClick={() => option.variant && onVariantChange(option.variant)}
                    />
                  ))}
                </Group>
              </VerticalSpacer>
            )}

            {optionTypeData.map(({ typeId, typeName, position, isColor, options, selectedOption }, index) => {
              if (!options.length || !selectedOption) {
                return null
              }

              if (shouldCombineColorOptions && isColor && index < 2) {
                return null
              }

              const showSizeChart = typeName.toLowerCase() === 'size' && !!offer.sizing

              return (
                <VerticalSpacer key={typeId} gap={8}>
                  <Group direction="horizontal" horizontalAlign="space-between">
                    <BodyText variant="medium">
                      <Colour value="neutral-two">{typeName}:</Colour> <b>{selectedOption.name}</b>
                    </BodyText>
                    {showSizeChart && <TextLink size="medium" onClick={onShowSizeChart}>
                      {intl.formatMessage({
                        id: 'Ok6w4XOG/o',
                        defaultMessage: 'Size chart',
                        description: 'Link text to view size chart in merchandise quick preview modal',
                      })}
                    </TextLink>}
                  </Group>
                  <Group direction="horizontal" gap={12} wrap="wrap">
                    {isColor && !shouldCombineColorOptions && options.map(option => {
                      const colorOption = option as App.MerchandiseOfferColourOption
                      return (
                        <MerchandiseColourOption
                          key={option.name}
                          interactive
                          size={20}
                          colors={[colorOption.colour]}
                          selected={option.name === selectedOption.name}
                          onClick={() => handleOptionSelect(position, option.name)}
                        />
                      )
                    })}
                    {(!isColor || shouldCombineColorOptions) && options.map(option => (
                      <ToggleButton
                        kind="secondary"
                        orientation="horizontal"
                        key={option.name}
                        isSelected={option.name === selectedOption.name}
                        onClick={() => handleOptionSelect(position, option.name)}
                        title={option.name}
                      />
                    ))}
                  </Group>
                </VerticalSpacer>
              )
            })}

            <VerticalSpacer gap={8}>
              <BodyText variant="medium" colour="neutral-two">
                {intl.formatMessage({
                  id: 'vKyDZGfQwS',
                  defaultMessage: 'Quantity:',
                  description: 'Label for quantity selector in merchandise quick preview modal',
                })}
              </BodyText>
              <Counter
                value={quantity}
                min={1}
                max={variant.inventory}
                onChange={setQuantity}
                disabled={!variant.inventory}
              />
              {quantity >= (variant.inventory ?? 0) && !!variant.inventory && (
                <MerchandiseMaxQuantityMessage align="start" />
              )}
            </VerticalSpacer>
          </DetailsSection>
        </ModalGrid>
      </ModalBody>
      <Footer $hasOverflow={hasOverflow} className={clsx({ 'has-shadow': hasOverflow && !hasReachedEnd })}>
        <FooterGrid>
          <FooterSpacer />
          <FooterButtons direction="horizontal" gap={8} horizontalAlign="stretch">
            <TextButton
              kind="secondary"
              startIcon={<LineShoppingCartIcon />}
              onClick={onAddToCart}
              disabled={!variant.inventory}
            >
              {intl.formatMessage({
                id: 'Zzp36JGne/',
                defaultMessage: 'Add to cart',
                description: 'Button text to add merchandise to shopping cart',
              })}
            </TextButton>
            <TextButton
              kind="primary"
              disabled={!variant.inventory}
              onClick={onBuyNow}
            >
              {intl.formatMessage({
                id: 'GgZJhBW+7Z',
                defaultMessage: 'Buy now',
                description: 'Button text to proceed to checkout with merchandise',
              })}
            </TextButton>
          </FooterButtons>
        </FooterGrid>
      </Footer>
    </ModalBase>
  )
}

export default MerchandiseQuickPreviewModal
